import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Chip,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import TableChartIcon from "@mui/icons-material/TableChart";
import SeedingIcon from "@mui/icons-material/Agriculture";
import RefreshIcon from "@mui/icons-material/Refresh";

const ChatSettingsSection = () => {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSetting, setEditingSetting] = useState(null);
  const [formData, setFormData] = useState({
    key: "",
    value: "",
    value_type: "string",
    category: "general",
    label: "",
    description: "",
  });
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const [filterCategory, setFilterCategory] = useState("all");
  const [schemaOpen, setSchemaOpen] = useState(false);
  const [dbSchema, setDbSchema] = useState([]);
  const [schemaLoading, setSchemaLoading] = useState(false);

  const categories = [
    { value: "rate_limiting", label: "Rate Limiting", color: "#f44336" },
    { value: "messages", label: "Messages", color: "#2196f3" },
    { value: "moderation", label: "Moderation", color: "#ff9800" },
    { value: "users", label: "Users", color: "#4caf50" },
    { value: "rooms", label: "Rooms", color: "#9c27b0" },
    { value: "system", label: "System", color: "#607d8b" },
    { value: "general", label: "General", color: "#795548" },
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSchema = async () => {
    setSchemaLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/chatroom/schema`
      );
      setDbSchema(response.data.schema);
    } catch (err) {
      console.error("Failed to fetch schema:", err);
    } finally {
      setSchemaLoading(false);
    }
  };

  // Fetch schema when the collapsible is opened for the first time
  useEffect(() => {
    if (schemaOpen && dbSchema.length === 0 && !schemaLoading) {
      fetchSchema();
    }
  }, [schemaOpen, dbSchema.length, schemaLoading]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/chatsettings`
      );
      setSettings(response.data.settings);
    } catch (err) {
      console.error("Failed to fetch settings:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (setting = null) => {
    if (setting) {
      setEditingSetting(setting);
      setFormData({
        key: setting.key,
        value: setting.value,
        value_type: setting.value_type,
        category: setting.category,
        label: setting.label,
        description: setting.description || "",
      });
    } else {
      setEditingSetting(null);
      setFormData({
        key: "",
        value: "",
        value_type: "string",
        category: "general",
        label: "",
        description: "",
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingSetting(null);
  };

  const handleSave = async () => {
    if (!formData.key.trim() || !formData.label.trim()) return;
    setSaving(true);

    try {
      if (editingSetting) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/admin/chatsettings/${editingSetting.id}`,
          formData
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/admin/chatsettings`,
          formData
        );
      }
      fetchSettings();
      handleCloseDialog();
    } catch (err) {
      console.error("Failed to save setting:", err);
      alert(err.response?.data?.error || "Failed to save setting");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this setting?")) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/admin/chatsettings/${id}`
      );
      fetchSettings();
    } catch (err) {
      console.error("Failed to delete setting:", err);
    }
  };

  const handleQuickEdit = async (setting, newValue) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/admin/chatsettings/${setting.id}`,
        { value: newValue }
      );
      fetchSettings();
    } catch (err) {
      console.error("Failed to update setting:", err);
    }
  };

  const handleSeed = async () => {
    if (!window.confirm("This will add default chatroom settings. Existing settings will be skipped. Continue?")) return;
    setSeeding(true);
    setSeedResult(null);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/admin/chatsettings/seed`
      );
      setSeedResult(response.data);
      fetchSettings();
    } catch (err) {
      console.error("Failed to seed settings:", err);
      setSeedResult({ error: "Failed to seed settings" });
    } finally {
      setSeeding(false);
    }
  };

  const getCategoryColor = (category) => {
    return categories.find((c) => c.value === category)?.color || "#795548";
  };

  const filteredSettings = settings.filter((s) =>
    filterCategory === "all" ? true : s.category === filterCategory
  );

  const groupedSettings = filteredSettings.reduce((acc, setting) => {
    if (!acc[setting.category]) {
      acc[setting.category] = [];
    }
    acc[setting.category].push(setting);
    return acc;
  }, {});

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress sx={{ color: "#fff" }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Collapsible Database Schema Reference */}
      <Paper sx={{ mb: 3, backgroundColor: "#16213e", borderRadius: 2, border: "1px solid #394867", overflow: "hidden" }}>
        <Box
          onClick={() => setSchemaOpen(!schemaOpen)}
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.02)" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <TableChartIcon sx={{ color: "#9e9e9e" }} />
            <Box>
              <Typography variant="subtitle1" sx={{ color: "#fff", fontWeight: 600 }}>
                Chatroom Database Schema
              </Typography>
              <Typography variant="caption" sx={{ color: "#666" }}>
                Reference for all chatroom-related tables
              </Typography>
            </Box>
          </Box>
          {schemaOpen ? <ExpandLess sx={{ color: "#9e9e9e" }} /> : <ExpandMore sx={{ color: "#9e9e9e" }} />}
        </Box>
        <Collapse in={schemaOpen}>
          <Box sx={{ p: 2, pt: 0, borderTop: "1px solid #394867" }}>
            {schemaLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={24} sx={{ color: "#9e9e9e" }} />
              </Box>
            ) : dbSchema.length === 0 ? (
              <Typography sx={{ color: "#666", textAlign: "center", py: 2 }}>
                No chatroom tables found in database
              </Typography>
            ) : (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {dbSchema.map((table) => (
                  <Grid item xs={12} md={6} key={table.tableName}>
                    <Paper sx={{ p: 2, backgroundColor: "#1a1a2e", borderRadius: 1, border: "1px solid #394867", height: "100%" }}>
                      <Typography variant="subtitle1" sx={{ color: table.color, fontWeight: 600, mb: 1 }}>
                        {table.displayName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#666", display: "block", mb: 1 }}>
                        {table.description}
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {table.columns.map((col) => (
                          <Chip key={col} label={col} size="small" sx={{ height: 18, fontSize: "0.65rem", backgroundColor: "#16213e", color: "#9e9e9e" }} />
                        ))}
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </Collapse>
      </Paper>

      {/* Action Bar */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 2, gap: 1 }}>
        <IconButton
          onClick={fetchSettings}
          disabled={loading}
          sx={{
            color: "#9e9e9e",
            "&:hover": { color: "#fff", backgroundColor: "#394867" },
          }}
          title="Refresh"
        >
          <RefreshIcon
            sx={{
              animation: loading ? "spin 1s linear infinite" : "none",
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }}
          />
        </IconButton>
        <Button
          variant="outlined"
          startIcon={seeding ? <CircularProgress size={16} sx={{ color: "#ff9800" }} /> : <SeedingIcon />}
          onClick={handleSeed}
          disabled={seeding}
          sx={{
            color: "#ff9800",
            borderColor: "#ff9800",
            "&:hover": { borderColor: "#ff9800", backgroundColor: "rgba(255, 152, 0, 0.1)" },
          }}
        >
          {seeding ? "Seeding..." : "Seed Defaults"}
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            backgroundColor: "#541b14",
            "&:hover": { backgroundColor: "#6d231a" },
          }}
        >
          Add Setting
        </Button>
      </Box>

      {/* Seed Result */}
      {seedResult && (
        <Paper sx={{ p: 1.5, mb: 2, backgroundColor: seedResult.error ? "#4a1515" : "#1b3a1b", borderRadius: 1, border: `1px solid ${seedResult.error ? "#ef5350" : "#4caf50"}` }}>
          <Typography variant="body2" sx={{ color: "#fff" }}>
            {seedResult.error ? seedResult.error : `Seeding complete: ${seedResult.inserted} inserted, ${seedResult.skipped} skipped (already exist)`}
          </Typography>
        </Paper>
      )}

      {/* Stats */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} md={2}>
          <Paper sx={{ p: 1.5, backgroundColor: "#16213e", borderRadius: 1, border: "1px solid #394867", textAlign: "center" }}>
            <Typography variant="h5" sx={{ color: "#4caf50", fontWeight: 700 }}>{settings.length}</Typography>
            <Typography variant="caption" sx={{ color: "#9e9e9e" }}>Total Settings</Typography>
          </Paper>
        </Grid>
        {categories.slice(0, 5).map((cat) => (
          <Grid item xs={6} md={2} key={cat.value}>
            <Paper
              sx={{
                p: 1.5,
                backgroundColor: "#16213e",
                borderRadius: 1,
                border: filterCategory === cat.value ? `2px solid ${cat.color}` : "1px solid #394867",
                textAlign: "center",
                cursor: "pointer",
                "&:hover": { borderColor: cat.color },
              }}
              onClick={() => setFilterCategory(filterCategory === cat.value ? "all" : cat.value)}
            >
              <Typography variant="h5" sx={{ color: cat.color, fontWeight: 700 }}>
                {settings.filter((s) => s.category === cat.value).length}
              </Typography>
              <Typography variant="caption" sx={{ color: "#9e9e9e" }}>{cat.label}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Filter */}
      <Paper sx={{ p: 2, mb: 2, backgroundColor: "#16213e", borderRadius: 2, border: "1px solid #394867" }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel sx={{ color: "#9e9e9e" }}>Filter by Category</InputLabel>
          <Select
            value={filterCategory}
            label="Filter by Category"
            onChange={(e) => setFilterCategory(e.target.value)}
            sx={{
              color: "#fff",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#394867" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#541b14" },
              "& .MuiSvgIcon-root": { color: "#9e9e9e" },
            }}
          >
            <MenuItem value="all">All Categories</MenuItem>
            {categories.map((cat) => (
              <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {/* Settings by Category */}
      {Object.entries(groupedSettings).map(([category, categorySettings]) => (
        <Paper key={category} sx={{ mb: 2, backgroundColor: "#16213e", borderRadius: 2, border: "1px solid #394867", overflow: "hidden" }}>
          <Box sx={{ p: 1.5, backgroundColor: "#0f1729", borderBottom: "1px solid #394867", display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: getCategoryColor(category) }} />
            <Typography variant="subtitle1" sx={{ color: "#fff", fontWeight: 600, textTransform: "capitalize" }}>
              {categories.find((c) => c.value === category)?.label || category}
            </Typography>
            <Chip label={categorySettings.length} size="small" sx={{ height: 20, fontSize: "0.7rem", backgroundColor: "#1a1a2e", color: "#9e9e9e" }} />
          </Box>
          <TableContainer>
            <Table size="small">
              <TableBody>
                {categorySettings.map((setting) => (
                  <tr key={setting.id} style={{ opacity: setting.is_active ? 1 : 0.5 }}>
                    <TableCell sx={{ color: "#fff", borderBottom: "1px solid #394867", py: 1.5, width: "25%" }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{setting.label}</Typography>
                      <Typography variant="caption" sx={{ color: "#666" }}>{setting.key}</Typography>
                    </TableCell>
                    <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867", py: 1.5, width: "35%" }}>
                      <Typography variant="caption">{setting.description || "-"}</Typography>
                    </TableCell>
                    <TableCell sx={{ borderBottom: "1px solid #394867", py: 1.5, width: "20%" }}>
                      {setting.value_type === "boolean" ? (
                        <Switch
                          size="small"
                          checked={setting.value === "true"}
                          onChange={(e) => handleQuickEdit(setting, e.target.checked ? "true" : "false")}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": { color: "#4caf50" },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#4caf50" },
                          }}
                        />
                      ) : setting.value_type === "number" ? (
                        <TextField
                          size="small"
                          type="number"
                          value={setting.value}
                          onChange={(e) => handleQuickEdit(setting, e.target.value)}
                          sx={{
                            width: 100,
                            "& .MuiOutlinedInput-root": {
                              color: "#fff",
                              "& fieldset": { borderColor: "#394867" },
                              "&:hover fieldset": { borderColor: "#541b14" },
                            },
                            "& .MuiOutlinedInput-input": { py: 0.5 },
                          }}
                        />
                      ) : (
                        <Typography variant="body2" sx={{ color: "#fff", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {setting.value}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ borderBottom: "1px solid #394867", py: 1.5 }}>
                      <Chip
                        label={setting.value_type}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.65rem",
                          backgroundColor: setting.value_type === "boolean" ? "#1b5e20" : setting.value_type === "number" ? "#0d47a1" : "#424242",
                          color: "#fff",
                        }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ borderBottom: "1px solid #394867", py: 1.5 }}>
                      <IconButton size="small" onClick={() => handleOpenDialog(setting)} sx={{ color: "#2196f3", p: 0.5 }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(setting.id)} sx={{ color: "#ef5350", p: 0.5 }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </tr>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ))}

      {filteredSettings.length === 0 && (
        <Paper sx={{ p: 4, backgroundColor: "#16213e", borderRadius: 2, border: "1px solid #394867", textAlign: "center" }}>
          <Typography variant="body1" sx={{ color: "#666" }}>
            No settings found. Click "Seed Defaults" to add default chatroom settings.
          </Typography>
        </Paper>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { backgroundColor: "#16213e", color: "#fff" } }}>
        <DialogTitle>{editingSetting ? "Edit Setting" : "Add New Setting"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Label"
                fullWidth
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: "#fff",
                    "& fieldset": { borderColor: "#394867" },
                    "&:hover fieldset": { borderColor: "#541b14" },
                    "&.Mui-focused fieldset": { borderColor: "#541b14" },
                  },
                  "& .MuiInputLabel-root": { color: "#9e9e9e" },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Key"
                fullWidth
                value={formData.key}
                onChange={(e) => setFormData({ ...formData, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "") })}
                helperText="lowercase_with_underscores"
                disabled={!!editingSetting}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: "#fff",
                    "& fieldset": { borderColor: "#394867" },
                    "&:hover fieldset": { borderColor: "#541b14" },
                    "&.Mui-focused fieldset": { borderColor: "#541b14" },
                  },
                  "& .MuiInputLabel-root": { color: "#9e9e9e" },
                  "& .MuiFormHelperText-root": { color: "#666" },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: "#9e9e9e" }}>Category</InputLabel>
                <Select
                  value={formData.category}
                  label="Category"
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  sx={{
                    color: "#fff",
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#394867" },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#541b14" },
                    "& .MuiSvgIcon-root": { color: "#9e9e9e" },
                  }}
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: "#9e9e9e" }}>Value Type</InputLabel>
                <Select
                  value={formData.value_type}
                  label="Value Type"
                  onChange={(e) => setFormData({ ...formData, value_type: e.target.value })}
                  sx={{
                    color: "#fff",
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#394867" },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#541b14" },
                    "& .MuiSvgIcon-root": { color: "#9e9e9e" },
                  }}
                >
                  <MenuItem value="string">String</MenuItem>
                  <MenuItem value="number">Number</MenuItem>
                  <MenuItem value="boolean">Boolean</MenuItem>
                  <MenuItem value="json">JSON</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              {formData.value_type === "boolean" ? (
                <FormControl fullWidth>
                  <InputLabel sx={{ color: "#9e9e9e" }}>Value</InputLabel>
                  <Select
                    value={formData.value}
                    label="Value"
                    onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                    sx={{
                      color: "#fff",
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#394867" },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#541b14" },
                      "& .MuiSvgIcon-root": { color: "#9e9e9e" },
                    }}
                  >
                    <MenuItem value="true">True</MenuItem>
                    <MenuItem value="false">False</MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  label="Value"
                  fullWidth
                  multiline={formData.value_type === "json" || formData.value_type === "string"}
                  rows={formData.value_type === "json" ? 3 : 1}
                  type={formData.value_type === "number" ? "number" : "text"}
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      color: "#fff",
                      "& fieldset": { borderColor: "#394867" },
                      "&:hover fieldset": { borderColor: "#541b14" },
                      "&.Mui-focused fieldset": { borderColor: "#541b14" },
                    },
                    "& .MuiInputLabel-root": { color: "#9e9e9e" },
                  }}
                />
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: "#fff",
                    "& fieldset": { borderColor: "#394867" },
                    "&:hover fieldset": { borderColor: "#541b14" },
                    "&.Mui-focused fieldset": { borderColor: "#541b14" },
                  },
                  "& .MuiInputLabel-root": { color: "#9e9e9e" },
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} sx={{ color: "#9e9e9e" }}>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={saving || !formData.key.trim() || !formData.label.trim()}
            sx={{ color: "#4caf50" }}
          >
            {saving ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChatSettingsSection;
